import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Send, Download, Filter, RefreshCw, CheckCircle, ArrowRight, AlertTriangle, Clock, DollarSign, Users, FileText, Shield } from 'react-feather';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import { partnersData } from '../../data/partnersData.ts';
import RangeDatePicker from '../../components/Layout/DatePicker.tsx';

// Types for payouts data
interface PayoutItem {
  id: string;
  merchantId: string;
  merchantName: string;
  type: 'Installments' | 'Acquiring';
  date: string;
  formattedDate: string;
  time: string;
  sales: number;
  refunds: number;
  fees: number;
  vat: number;
  payoutAmount: number;
  balance: number;
  currency: string;
  status: 'Paid' | 'Unpaid' | 'In progress' | 'Reversed';
}

// Currency mapping
const currencyMap: { [key: string]: string } = {
  'UAE': 'AED',
  'KSA': 'SAR',
  'Qatar': 'QAR',
  'Kuwait': 'KWD',
  'Bahrain': 'BHD',
  'Egypt': 'EGP',
  'Oman': 'OMR',
};

// Currency conversion rates (for demonstration)
const currencyRates: { [key: string]: number } = {
  'AED': 3.67,
  'SAR': 3.75,
  'QAR': 3.64,
  'KWD': 0.31,
  'BHD': 0.38,
  'EGP': 30.9,
  'OMR': 0.38,
};

// Custom Dropdown Component similar to PartnersPage
const DropdownFilter: React.FC<{
  label: string;
  options: string[];
  selectedValue: string;
  onChange: (value: string) => void;
}> = ({ label, options, selectedValue, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        style={{
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          backgroundColor: 'white',
          fontSize: '14px',
          color: '#374151',
          cursor: 'pointer',
          minWidth: '140px',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6px',
          transition: 'border-color 0.15s ease-in-out'
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
      >
        {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
        {/* Custom Arrow */}
        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
        </svg>
      </button>
      {isOpen && (
        <div style={{ 
          position: 'absolute', 
          top: 'calc(100% + 4px)', 
          left: 0, 
          backgroundColor: 'white', 
          border: '1px solid #e5e7eb', 
          borderRadius: '6px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', 
          zIndex: 10, 
          minWidth: '160px', 
          padding: '4px 0',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {options.map(option => (
              <li
                key={option}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#374151',
                  backgroundColor: hoveredOption === option ? '#f9fafb' : 'white'
                }}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
              >
                {option}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// NEW: Status Badge Component (similar to TransactionsPage)
const StatusBadge: React.FC<{ status: PayoutItem['status'] }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'capitalize',
    display: 'inline-block',
  };

  switch (status) {
    case 'Paid': // Mapped from 'Completed'
      styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' };
      break;
    case 'In progress': // Mapped from 'Processing'
      styles = { ...styles, backgroundColor: '#fefce8', color: '#d97706' };
      break;
    case 'Unpaid': // New Status
      styles = { ...styles, backgroundColor: '#f1f5f9', color: '#475569' }; // Light gray
      break;
    case 'Reversed': // Mapped from 'Failed' (conceptually similar)
      styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' };
      break;
    default:
      styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' };
      break;
  }

  return <span style={styles}>{status}</span>;
};

// Filter Component
const PayoutFilters: React.FC<{
  filters: {
    country: string | null;
    type: string | null;
    status: string | null;
  };
  onFilterChange: (filters: Partial<{ country: string | null; type: string | null; status: string | null }>) => void;
  selectedDateRange: string;
  onDateRangeChange: (range: string) => void;
  customStartDate: Date | null;
  customEndDate: Date | null;
  onCustomDateChange: (dates: [Date | null, Date | null]) => void;
  isDatePickerVisible: boolean;
  countries: string[];
  types: string[];
  statuses: string[];
  onNewPayout: () => void;
}> = ({
  filters,
  onFilterChange,
  selectedDateRange,
  onDateRangeChange,
  customStartDate,
  customEndDate,
  onCustomDateChange,
  isDatePickerVisible,
  countries,
  types,
  statuses,
  onNewPayout
}) => {
  // Handle country change
  const handleCountryChange = (country: string) => {
    onFilterChange({ country: country === 'All Countries' ? null : country });
  };

  // Handle type change
  const handleTypeChange = (type: string) => {
    onFilterChange({ type: type === 'All Types' ? null : type });
  };

  // NEW: Handle status change
  const handleStatusChange = (status: string) => {
    onFilterChange({ status: status === 'All Statuses' ? null : status });
  };

  // Date range options
  const dateRangeOptions = ['All Time', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom date'];

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      marginBottom: '24px',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Date Dropdown Filter */}
        <DropdownFilter
          label="Date"
          options={dateRangeOptions}
          selectedValue={selectedDateRange}
          onChange={onDateRangeChange}
        />
        {isDatePickerVisible && (
          <div style={{ marginLeft: '-6px' /* Adjust positioning */ }}>
            <RangeDatePicker
              startDate={customStartDate}
              endDate={customEndDate}
              onChange={onCustomDateChange}
              id="payouts-date-picker-trigger" // Use a unique ID if needed
            />
          </div>
        )}

        {/* Country Dropdown - Using Custom Component */}
        <DropdownFilter
          label="Country"
          options={countries}
          selectedValue={filters.country || 'All Countries'}
          onChange={handleCountryChange}
        />

        {/* Type Dropdown - Using Custom Component */}
        <DropdownFilter
          label="Type"
          options={types}
          selectedValue={filters.type || 'All Types'}
          onChange={handleTypeChange}
        />

        {/* NEW: Status Dropdown Filter */}
        <DropdownFilter
          label="Status"
          options={statuses}
          selectedValue={filters.status || 'All Statuses'}
          onChange={handleStatusChange}
        />
      </div>

      {/* Buttons container */}
      <div style={{ display: 'flex', gap: '12px' }}>
        {/* Export Button */}
        <button
          style={{
            backgroundColor: '#fff',
            color: '#1f2937',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '10px 15px', // Unified padding
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // Unified shadow
            transition: 'background-color 0.2s ease, border-color 0.2s ease', // Unified transition
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
        >
          <Download size={16} /> {/* Unified icon size */}
          Export in CSV
        </button>
        {/* New Payout Button */}
        <button
          style={{
            backgroundColor: '#fff',
            color: '#1f2937',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '10px 15px', // Unified padding
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // Unified shadow
            transition: 'background-color 0.2s ease, border-color 0.2s ease', // Unified transition
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
          onClick={onNewPayout}
        >
          <Send size={16} /> {/* Unified icon size */}
          New Payout
        </button>
      </div>
    </div>
  );
};

// Payout Table Component
const PayoutTable: React.FC<{ 
  payouts: PayoutItem[]; 
  partnerDetailsMap: { [key: string]: { logoUrl?: string; location: string } }; // Use 'key: string' to be more general
  onRowClick: (payout: PayoutItem) => void;
}> = ({ payouts, partnerDetailsMap, onRowClick }) => {
  // Format currency function
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // NEW: State for hovered row
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        borderSpacing: 0,
        fontSize: '13px', // Match TransactionsPage
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' /* Match TransactionsPage */ }}>
            <th style={{ textAlign: 'left', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Date</th>
            <th style={{ textAlign: 'left', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, minWidth: '200px' }}>Merchant</th>
            <th style={{ textAlign: 'left', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Type</th>
            <th style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Sales</th>
            <th style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Refunds</th>
            <th style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Fees</th>
            <th style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>VAT</th>
            <th style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Balance</th>
            <th style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>Payout</th>
            <th style={{ textAlign: 'center', padding: '12px 16px', color: '#6b7280', fontWeight: 500, position: 'sticky', top: 0 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((payout) => (
            <tr 
              key={payout.id}
              style={{ 
                cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: hoveredRowId === payout.id ? '#f9fafb' : 'transparent', // Match TransactionsPage hover
                transition: 'background-color 0.1s ease-in-out', // Match TransactionsPage transition
                height: '50px' // Increased height slightly to accommodate 2 lines + logo comfortably
              }}
              onMouseEnter={() => setHoveredRowId(payout.id)} // Use ID for hover state
              onMouseLeave={() => setHoveredRowId(null)} // Clear hover state
              onClick={() => onRowClick(payout)} // NEW: Add click handler
            >
              <td style={{ padding: '7px 16px' }}>
                <div style={{ fontWeight: 400, color: '#111827' }}>{payout.formattedDate}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{payout.time}</div>
              </td>
              <td style={{ padding: '7px 8px', display: 'flex', alignItems: 'center' }}>
                {
                  partnerDetailsMap[payout.merchantId]?.logoUrl ? (
                    <img
                      src={partnerDetailsMap[payout.merchantId].logoUrl}
                      alt={`${payout.merchantName} logo`}
                      style={{ marginRight: '10px', width: '24px', height: '24px', borderRadius: '50%', objectFit: 'contain', border: '1px solid #f3f4f6' }}
                      onError={(e) => {
                        const imgElement = e.target as HTMLImageElement;
                        imgElement.style.display = 'none';
                        const fallbackSpan = imgElement.nextElementSibling as HTMLElement | null;
                        if (fallbackSpan && fallbackSpan.classList.contains('logo-fallback')) {
                          fallbackSpan.style.display = 'flex';
                          fallbackSpan.style.marginRight = '10px';
                        }
                      }}
                    />
                  ) : null
                }
                <span
                  className="logo-fallback"
                  style={{
                    marginRight: '10px',
                    display: partnerDetailsMap[payout.merchantId]?.logoUrl ? 'none' : 'flex',
                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#f3f4f6',
                    alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase'
                  }}
                >
                  {payout.merchantName.split(' ').map(n => n?.[0]).filter(Boolean).slice(0, 2).join('')}
                </span>
                <div>
                  <div style={{ fontWeight: 400, color: '#111827' /* Match TransactionsPage */ }}>{payout.merchantName}</div>
                  {partnerDetailsMap[payout.merchantId]?.location && (
                    <div style={{ fontSize: '12px', color: '#6b7280' /* Match TransactionsPage */ }}>
                      {partnerDetailsMap[payout.merchantId].location}
                    </div>
                  )}
                </div>
              </td>
              <td style={{ padding: '7px 16px', color: '#111827' }}>{payout.type}</td>
              <td style={{ padding: '7px 16px', textAlign: 'right', color: '#111827', whiteSpace: 'nowrap' }}>
                {payout.currency} {formatCurrency(payout.sales, payout.currency)}
              </td>
              <td style={{ padding: '7px 16px', textAlign: 'right', color: '#111827', whiteSpace: 'nowrap' }}>
                {payout.currency} {formatCurrency(payout.refunds, payout.currency)}
              </td>
              <td style={{ padding: '7px 16px', textAlign: 'right', color: '#111827', whiteSpace: 'nowrap' }}>
                {payout.currency} {formatCurrency(payout.fees, payout.currency)}
              </td>
              <td style={{ padding: '7px 16px', textAlign: 'right', color: '#111827', whiteSpace: 'nowrap' }}>
                {payout.currency} {formatCurrency(payout.vat, payout.currency)}
              </td>
              <td style={{ padding: '7px 16px', textAlign: 'right', color: payout.balance < 0 ? '#ef4444' : '#111827', whiteSpace: 'nowrap' }}>
                {payout.currency} {formatCurrency(payout.balance, payout.currency)}
              </td>
              <td style={{ padding: '7px 16px', textAlign: 'right', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>
                {payout.currency} {formatCurrency(payout.payoutAmount, payout.currency)}
              </td>
              {/* NEW: Status Cell */}
              <td style={{ padding: '7px 16px', textAlign: 'center' }}>
                <StatusBadge status={payout.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// NEW: Payout Process Modal Components
// Modal Backdrop component
interface ModalBackdropProps {
  children: React.ReactNode;
  onClose: () => void;
  preventClose?: boolean;
}

const ModalBackdrop: React.FC<ModalBackdropProps> = ({ children, onClose, preventClose = false }) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal content
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (preventClose) return;
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, preventClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '100%',
          maxWidth: '650px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </div>
    </div>
  );
};

// Payout Summary Modal
interface PayoutSummaryModalProps {
  onClose: () => void;
  onConfirm: () => void;
  unpaidPayouts: PayoutItem[];
}

const PayoutSummaryModal: React.FC<PayoutSummaryModalProps> = ({ onClose, onConfirm, unpaidPayouts }) => {
  // Calculate summary statistics
  const summary = useMemo(() => {
    // Generate a large random number for merchants if there are unpaid payouts
    const rawMerchantCount = Array.from(new Set(unpaidPayouts.map(p => p.merchantName))).length;
    const displayMerchants = rawMerchantCount > 0 
      ? Math.floor(15000 + Math.random() * 30000) // Random number between 15k and 45k
      : 0;
    
    let totalAmountUSD = 0; // NEW: Track total in USD
    const byType: { [key: string]: { count: number; total: number; totalUSD: number; currencies: Set<string> } } = {}; // Added totalUSD
    const byCurrency: { [key: string]: { count: number; total: number; types: Set<string> } } = {}; 

    unpaidPayouts.forEach(payout => {
      // Convert to USD (assuming USD is the base, rate 1)
      const amountUSD = payout.payoutAmount / (currencyRates[payout.currency] || 1); 
      totalAmountUSD += amountUSD;

      // Aggregate by type
      if (!byType[payout.type]) {
        byType[payout.type] = { count: 0, total: 0, totalUSD: 0, currencies: new Set() };
      }
      byType[payout.type].count += 1;
      byType[payout.type].total += payout.payoutAmount; // Keep original total for potential display if needed
      byType[payout.type].totalUSD += amountUSD; // Accumulate USD total for the type
      byType[payout.type].currencies.add(payout.currency);

      // Aggregate by currency
      if (!byCurrency[payout.currency]) {
        byCurrency[payout.currency] = { count: 0, total: 0, types: new Set() };
      }
      byCurrency[payout.currency].count += 1;
      byCurrency[payout.currency].total += payout.payoutAmount;
      byCurrency[payout.currency].types.add(payout.type);
    });

    // Get the most recent and oldest payout dates
    const dates = unpaidPayouts.map(p => new Date(p.date));
    const oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const newestDate = new Date(Math.max(...dates.map(d => d.getTime())));

    // Format dates
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return {
      totalCount: unpaidPayouts.length,
      merchants: displayMerchants, // Use the generated large number
      types: Object.entries(byType).map(([type, data]) => ({
        type,
        count: data.count,
        total: data.total, // Original total (multi-currency)
        totalUSD: data.totalUSD, // NEW: USD total
        currencies: Array.from(data.currencies).sort()
      })),
      currencies: Object.entries(byCurrency)
        .filter(([currency]) => currency !== 'EGP') // <<< Remove EGP here
        .map(([currency, data]) => ({
          currency,
          count: data.count,
          total: data.total,
          types: Array.from(data.types).sort()
        })).sort((a, b) => b.total - a.total), 
      dateRange: `${formatDate(oldestDate)} - ${formatDate(newestDate)}`,
      totalAmount: unpaidPayouts.reduce((sum, p) => sum + p.payoutAmount, 0), // Original total (multi-currency)
      totalAmountUSD: totalAmountUSD // NEW: USD total
    };
  }, [unpaidPayouts]);

  // Function to format currency (now specifically for USD)
  const formatUSD = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Function to format non-USD currency
  const formatCurrency = (amount: number, currencyCode: string) => {
    // Basic formatting, consider adding specific locale/style if needed
    return `${currencyCode} ${new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)}`;
  };

  return (
    <>
      {/* Modal Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>New Payout</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Review and confirm all pending payouts</p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px', // Reduced padding for a tighter look
            color: '#6b7280',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease-in-out' // Added transition
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'} // Slightly darker gray on hover
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Modal Body - Adjusted padding and added overflow control */}
      <div style={{ padding: '24px', maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}> {/* Adjusted max height and padding */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}> {/* Grid layout for stats */}
          {/* Stat Card: Total Merchants */}
          <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ backgroundColor: '#e6f7f0', padding: '8px', borderRadius: '6px' }}> {/* Light Green Icon background */}
              <Users size={20} style={{ color: '#00875a' }} /> {/* Dark Green Icon */}
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Total Merchants</div>
              <div style={{ fontSize: '20px', fontWeight: 600, color: '#111827' }}>{summary.merchants}</div> {/* Larger number */}
            </div>
          </div>
          
          {/* Stat Card: Payout Cycle */}
          <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ backgroundColor: '#e6f7f0', padding: '8px', borderRadius: '6px' }}> {/* Light Green Icon background */}
              <FileText size={20} style={{ color: '#00875a' }} /> {/* Dark Green Icon */}
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Payout Cycle</div>
              <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>{summary.dateRange}</div> {/* Slightly smaller font for date */}
            </div>
          </div>
          
          {/* Stat Card: Total Payout Amount */}
          <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ backgroundColor: '#e6f7f0', padding: '8px', borderRadius: '6px' }}> {/* Light Green Icon background */}
              <DollarSign size={20} style={{ color: '#00875a' }} /> {/* Dark Green Icon */}
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Total Payout Amount</div>
              <div style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }}> {/* Larger number */}
                {formatUSD(summary.totalAmountUSD)} {/* Display USD total */}
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown by Type */}
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px', marginTop: '28px' }}>Breakdown by Type</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}> {/* Grid layout */}
          {summary.types.map((typeData, index) => (
            <div key={`type-${index}`} style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '16px', 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>{typeData.type}</span>
                <span style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}> {/* Larger number */}
                  {formatUSD(typeData.totalUSD)} {/* Display USD total */}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}> {/* Smaller text */}
                {typeData.count} {typeData.count === 1 ? 'payout' : 'payouts'}
              </div>
            </div>
          ))}
        </div>

        {/* NEW: Breakdown by Currency */}
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px', marginTop: '28px' }}>Breakdown by Currency</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}> {/* Grid layout */}
          {summary.currencies.map((currencyData, index) => (
            <div key={`currency-${index}`} style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '16px', 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>{currencyData.currency}</span>
                <span style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}> {/* Larger number */}
                  {formatCurrency(currencyData.total, currencyData.currency)} {/* Display original currency */}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', textAlign: 'right', marginBottom: '8px' }}> {/* Smaller text */}
                {currencyData.count} {currencyData.count === 1 ? 'payout' : 'payouts'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Footer - Adjusted padding and button styles */}
      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #e5e7eb', 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: '12px',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 16px', // Match ReportsPage button style
            borderRadius: '8px', // Match ReportsPage button style
            border: '1px solid #d1d5db', // Match ReportsPage button style
            backgroundColor: 'white',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151', // Match ReportsPage button style
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background-color 0.15s ease-in-out' // Added transition
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'} // Subtle hover
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            padding: '10px 16px', // Match ReportsPage button style
            borderRadius: '8px', // Match ReportsPage button style
            border: '1px solid #00875a', // Dark Green border
            backgroundColor: '#00875a', // Dark Green background
            fontSize: '14px',
            fontWeight: 500,
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background-color 0.15s ease-in-out' // Added transition
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#006a4a'} // Slightly darker green on hover
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00875a'} // Reset to dark green
        >
          Confirm & Process
          <ArrowRight size={16} />
        </button>
      </div>
    </>
  );
};

// Payout Processing Modal
interface ProcessingStep {
  name: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'processing' | 'success';
}

interface PayoutProcessingModalProps {
  onFinish: () => void;
  processedCount: number;
  processedAmountUSD: number;
}

const PayoutProcessingModal: React.FC<PayoutProcessingModalProps> = ({ onFinish, processedCount, processedAmountUSD }) => {
  const [steps, setSteps] = useState<ProcessingStep[]>([
    {
      name: 'balance',
      label: 'Checking Previous Balances',
      description: 'Verifying all merchant account balances',
      icon: <DollarSign size={20} />,
      status: 'processing'
    },
    {
      name: 'transactions',
      label: 'Reconciling Transactions',
      description: 'Matching transactions with payment records',
      icon: <FileText size={20} />,
      status: 'pending'
    },
    {
      name: 'wallets',
      label: 'Offsetting Wallets',
      description: 'Updating wallet balances across all accounts',
      icon: <Users size={20} />,
      status: 'pending'
    },
    {
      name: 'risk',
      label: 'Checking Risk Holds',
      description: 'Reviewing for any risk-related payment holds',
      icon: <Shield size={20} />,
      status: 'pending'
    }
  ]);

  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [completionTimestamp, setCompletionTimestamp] = useState<string | null>(null);

  // Simulate the processing steps
  useEffect(() => {
    if (currentStep >= steps.length) {
      // All steps are completed
      setTimeout(() => {
        setIsComplete(true);
        setCompletionTimestamp(new Date().toLocaleString());
      }, 1000);
      return;
    }

    // Process the current step
    const timer = setTimeout(() => {
      setSteps(prev => {
        const newSteps = [...prev];
        newSteps[currentStep].status = 'success';
        if (currentStep + 1 < steps.length) {
          newSteps[currentStep + 1].status = 'processing';
        }
        return newSteps;
      });
      setCurrentStep(prev => prev + 1);
    }, 2000); // Each step takes 2 seconds

    return () => clearTimeout(timer);
  }, [currentStep, steps.length]);

  // NEW: Helper to format USD
  const formatUSD = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <>
      {/* Modal Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
          {isComplete ? 'Payouts Successful' : 'Processing Payouts'}
        </h2>
        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
          {isComplete 
            ? 'All payouts have been successfully processed' 
            : 'Please wait while we process the payouts'
          }
        </p>
      </div>

      {/* Modal Body */}
      <div style={{ padding: '24px 32px', minHeight: '300px' }}>
        {isComplete ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#ecfdf5', // Keep the light green background for contrast
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 20px',
              overflow: 'hidden' // Ensure logo stays within circle
            }}>
              {/* MODIFIED: Replace CheckCircle with Wafeq logo */}
              <img 
                src="https://raed.vc/wp-content/uploads/2023/01/Wafeq_logo.png"
                alt="Wafeq Logo"
                style={{ width: '60px', height: 'auto', objectFit: 'contain' }} // Adjusted size
              />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
              Payouts Successfully Sent
            </h3>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '20px', maxWidth: '450px', margin: '0 auto 20px' }}>
              {/* MODIFIED: Added count and removed inline logo */}
              All {processedCount} {processedCount === 1 ? 'payout' : 'payouts'} have been processed and sent to Wafeq ERP for final distribution.
            </p>
            {/* NEW: Container for technical details */}
            <div style={{
              backgroundColor: '#f9fafb', 
              border: '1px solid #e5e7eb',
              borderRadius: '8px', 
              padding: '16px', 
              display: 'inline-block',
              textAlign: 'left',
              margin: '0 auto',
              minWidth: '250px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Reference ID</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>PAY-{Date.now().toString().slice(-9)}</div>
              </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                 <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Processed</div>
                 <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{formatUSD(processedAmountUSD)}</div>
               </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                 <div style={{ fontSize: '14px', color: '#6b7280' }}>Completed At</div>
                 <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{completionTimestamp}</div>
               </div>
            </div>
          </div>
        ) : (
          <div>
            {steps.map((step, index) => (
              <div 
                key={step.name}
                style={{ 
                  display: 'flex', 
                  marginBottom: index === steps.length - 1 ? 0 : '24px',
                  position: 'relative'
                }}
              >
                {/* Step indicator and icon */}
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 
                    step.status === 'success' ? '#ecfdf5' : 
                    step.status === 'processing' ? '#eff6ff' : 
                    '#f3f4f6',
                  color: 
                    step.status === 'success' ? '#10b981' : 
                    step.status === 'processing' ? '#3b82f6' : 
                    '#9ca3af',
                  marginRight: '16px',
                  flexShrink: 0
                }}>
                  {step.status === 'success' ? (
                    <CheckCircle size={20} />
                  ) : step.status === 'processing' ? (
                    <div style={{ animation: 'spin 2s linear infinite' }}>
                      <RefreshCw size={20} />
                    </div>
                  ) : (
                    step.icon
                  )}
                </div>
                
                {/* Step content */}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    color: 
                      step.status === 'success' ? '#10b981' : 
                      step.status === 'processing' ? '#3b82f6' : 
                      '#6b7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {step.label}
                    {step.status === 'processing' && (
                      <span style={{ fontSize: '14px', marginLeft: '8px', color: '#3b82f6' }}>
                        Processing...
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Footer */}
      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #e5e7eb', 
        display: 'flex', 
        justifyContent: 'flex-end',
        backgroundColor: '#f9fafb'
      }}>
        {isComplete && (
          <button
            onClick={onFinish}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #4f46e5',
              backgroundColor: '#4f46e5',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4338ca'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
          >
            Close
          </button>
        )}
      </div>

      {/* Add keyframe animation for spinning icon */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

// NEW: Transaction interface for the payout details panel
interface PayoutTransaction {
  id: string;
  type: 'Order' | 'Refund' | 'Payout sent' | 'Payout created';
  orderId?: string;
  date: string;
  time: string;
  amount: number;
  currency: string;
}

// NEW: PayoutDetailsPanel Component
interface PayoutDetailsPanelProps {
  payout: PayoutItem | null;
  onClose: () => void;
  isVisible: boolean;
  contentFadeKey: string;
}

const PayoutDetailsPanel: React.FC<PayoutDetailsPanelProps> = ({ payout, onClose, isVisible, contentFadeKey }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [contentVisible, setContentVisible] = useState(true);
  
  // Handle payout changes with animation
  useEffect(() => {
    setContentVisible(true);
  }, [contentFadeKey]);
  
  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip outside click handling if we clicked on a table row
      if ((event.target as Element).closest('tr')) {
        return;
      }
      
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Only add the event listener if the panel is visible
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Early return if no payout is selected
  if (!payout) return null;

  // Format currency function
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Generate mock transactions for the payout
  const generateMockTransactions = (): PayoutTransaction[] => {
    const transactions: PayoutTransaction[] = [];
    const payoutDate = new Date(payout.date);
    
    // Add payout sent transaction
    transactions.push({
      id: `sent-${payout.id}`,
      type: 'Payout sent',
      date: payout.formattedDate,
      time: '12:00 AM', // Mock time
      amount: -payout.payoutAmount, // Negative for sent
      currency: payout.currency
    });

    // Add payout created transaction
    transactions.push({
      id: `created-${payout.id}`,
      type: 'Payout created',
      date: payout.formattedDate,
      time: '11:59 AM', // Mock time, slightly earlier
      amount: payout.payoutAmount,
      currency: payout.currency
    });

    // Generate 2-5 orders
    const orderCount = 2 + Math.floor(Math.random() * 4);
    for (let i = 0; i < orderCount; i++) {
      const orderDate = new Date(payoutDate);
      orderDate.setHours(orderDate.getHours() - (i + 1)); // Orders hours before payout
      
      const orderAmount = Math.round((payout.payoutAmount / orderCount) * (0.8 + Math.random() * 0.4) * 100) / 100;
      
      transactions.push({
        id: `order-${payout.id}-${i}`,
        type: 'Order',
        orderId: `#${583400000 + Math.floor(Math.random() * 1000000)}`,
        date: orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        time: `${(orderDate.getHours() % 12) || 12}:${orderDate.getMinutes().toString().padStart(2, '0')} ${orderDate.getHours() >= 12 ? 'PM' : 'AM'}`,
        amount: orderAmount,
        currency: payout.currency
      });
    }

    // Add a refund for ~20% of payouts
    if (Math.random() < 0.2) {
      const refundDate = new Date(payoutDate);
      refundDate.setHours(refundDate.getHours() - Math.floor(Math.random() * 5)); // Refund hours before payout
      
      const refundAmount = -Math.round(payout.payoutAmount * 0.15 * 100) / 100; // Refund about 15% of payout
      
      transactions.push({
        id: `refund-${payout.id}`,
        type: 'Refund',
        orderId: `#${8128000 + Math.floor(Math.random() * 1000)}r${Math.floor(Math.random() * 100)}`,
        date: refundDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        time: `${(refundDate.getHours() % 12) || 12}:${refundDate.getMinutes().toString().padStart(2, '0')} ${refundDate.getHours() >= 12 ? 'PM' : 'AM'}`,
        amount: refundAmount,
        currency: payout.currency
      });
    }

    // Sort by date/time (newest first)
    return transactions.sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time}`);
      const dateB = new Date(`${b.date} ${b.time}`);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const transactions = generateMockTransactions();

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        top: '64px',
        right: 0,
        width: '380px',
        height: 'calc(100vh - 64px)',
        backgroundColor: 'white',
        boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        overflowY: 'auto',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out',
        padding: '24px',
      }}
    >
      {/* Header with close button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '24px', 
          fontWeight: 600, 
          color: '#111827',
          fontFamily: 'inherit'
        }}>
          Payout Details
        </h2>
        <button 
          onClick={onClose} 
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Panel Content with Fade Animation */}
      <div style={{
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      }}>
        {/* Payout Status Badge */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <StatusBadge status={payout.status} />
        </div>

        {/* Amount Section */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ 
            color: '#6b7280', 
            fontSize: '16px', 
            margin: '0 0 8px 0',
            fontWeight: 500,
            fontFamily: 'inherit'
          }}>
            Payout Amount
          </p>
          <h3 style={{ 
            fontSize: '32px', 
            fontWeight: 600, 
            margin: 0,
            color: '#111827',
            fontFamily: 'inherit'
          }}>
            {payout.currency} {formatCurrency(payout.payoutAmount, payout.currency)}
          </h3>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        {/* Payout Details */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', rowGap: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Merchant</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {payout.merchantName}
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Type</p>
              <div style={{ 
                backgroundColor: '#f3f4f6',
                color: '#374151',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'inline-block',
                fontFamily: 'inherit'
              }}>
                {payout.type}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', rowGap: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Date & Time</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {payout.formattedDate}, {payout.time}
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Reference</p>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#9ca3af" style={{ width: '16px', height: '16px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5-3.9 19.5m-2.1-19.5-3.9 19.5" />
                </svg>
                <span style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  PAY-{payout.id.substring(0, 8)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payout Summary Section */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ 
            fontSize: '16px', 
            color: '#111827', 
            fontWeight: 600, 
            margin: '0 0 16px 0',
            fontFamily: 'inherit'
          }}>
            Payout Summary
          </h4>
          
          <div style={{ 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '12px' 
            }}>
              <span style={{ color: '#6b7280', fontFamily: 'inherit' }}>Sales</span>
              <span style={{ color: '#111827', fontWeight: 500, fontFamily: 'inherit' }}>{payout.currency} {formatCurrency(payout.sales, payout.currency)}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span style={{ color: '#6b7280', fontFamily: 'inherit' }}>Refunds</span>
              <span style={{ color: '#ef4444', fontWeight: 500, fontFamily: 'inherit' }}>{payout.currency} {formatCurrency(payout.refunds, payout.currency)}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span style={{ color: '#6b7280', fontFamily: 'inherit' }}>Fees</span>
              <span style={{ color: '#ef4444', fontWeight: 500, fontFamily: 'inherit' }}>{payout.currency} {formatCurrency(payout.fees, payout.currency)}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span style={{ color: '#6b7280', fontFamily: 'inherit' }}>VAT</span>
              <span style={{ color: '#ef4444', fontWeight: 500, fontFamily: 'inherit' }}>{payout.currency} {formatCurrency(payout.vat, payout.currency)}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              borderTop: '1px solid #e5e7eb',
              paddingTop: '12px',
              marginTop: '6px'
            }}>
              <span style={{ color: '#111827', fontWeight: 500, fontFamily: 'inherit' }}>Total Payout</span>
              <span style={{ color: '#111827', fontWeight: 600, fontFamily: 'inherit' }}>{payout.currency} {formatCurrency(payout.payoutAmount, payout.currency)}</span>
            </div>
          </div>
        </div>

        {/* Transaction List Section */}
        <div>
          <h4 style={{ 
            fontSize: '16px', 
            color: '#111827', 
            fontWeight: 600, 
            margin: '0 0 16px 0',
            fontFamily: 'inherit'
          }}>
            Transactions
          </h4>
          
          <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {transactions.map((transaction, index) => (
              <div 
                key={transaction.id}
                style={{ 
                  padding: '12px 16px',
                  display: 'flex', 
                  justifyContent: 'space-between',
                  borderBottom: index < transactions.length - 1 ? '1px solid #f3f4f6' : 'none',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>
                    {transaction.date} • {transaction.time}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ 
                      backgroundColor: 
                        transaction.type === 'Payout sent' ? '#f0f9ff' : 
                        transaction.type === 'Payout created' ? '#f0fdf4' :
                        transaction.type === 'Order' ? '#f3f4f6' :
                        '#fef2f2', // Refund
                      color: 
                        transaction.type === 'Payout sent' ? '#0284c7' : 
                        transaction.type === 'Payout created' ? '#16a34a' :
                        transaction.type === 'Order' ? '#374151' :
                        '#dc2626', // Refund
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}>
                      {transaction.type}
                    </span>
                    {transaction.orderId && (
                      <span>{transaction.orderId}</span>
                    )}
                  </div>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: 500, 
                  color: transaction.amount >= 0 ? '#111827' : '#ef4444',
                  alignSelf: 'center'
                }}>
                  {transaction.amount >= 0 ? '' : '-'} {transaction.currency} {formatCurrency(Math.abs(transaction.amount), transaction.currency)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payout ID Footer */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginTop: '40px',
          color: '#9ca3af',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '16px', height: '16px', marginRight: '6px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          Payout ID: {payout.id.substring(0, 8)}
        </div>
      </div>
    </div>
  );
};

const PayoutsPage: React.FC = () => {
  // State for payouts data
  const [payouts, setPayouts] = useState<PayoutItem[]>([]);
  const [filteredPayouts, setFilteredPayouts] = useState<PayoutItem[]>([]);
  
  // Filters state - updated
  const [filters, setFilters] = useState({
    country: null as string | null,
    type: null as string | null,
    status: null as string | null,
  });

  // Date filter state management
  const [selectedDateRange, setSelectedDateRange] = useState<string>('All Time');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState<boolean>(false);

  // NEW: Payout flow modals state
  const [isPayoutSummaryModalOpen, setIsPayoutSummaryModalOpen] = useState(false);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  // NEW: State to hold details for the processing modal
  const [payoutProcessDetails, setPayoutProcessDetails] = useState<{ count: number; amountUSD: number }>({ count: 0, amountUSD: 0 });
  
  // Derive Country and Type options
  const countries = useMemo(() => [
    'All Countries', 
    ...Array.from(new Set(partnersData.map(p => p.location)))
  ], []);

  const types = useMemo(() => [
    'All Types', 
    'Installments', 
    'Acquiring'
  ], []);

  // Derive Status options
  const statusOptions = useMemo(() => [
    'All Statuses',
    'Paid',
    'Unpaid',
    'In progress',
    'Reversed'
  ], []);

  // Create a map for easy lookup of partner logo and location
  const partnerDetailsMap = useMemo(() => {
    const map: { [key: string]: { logoUrl?: string; location: string } } = {};
    partnersData.forEach(partner => {
      map[partner.name] = { logoUrl: partner.logoUrl ?? undefined, location: partner.location };
    });
    return map;
  }, []);

  // Generate mock payout data
  useEffect(() => {
    const payoutTypes = ['Installments', 'Acquiring'];
    const statuses: PayoutItem['status'][] = ['Paid', 'Unpaid', 'In progress', 'Reversed'];
    const mockPayouts: PayoutItem[] = [];
    
    // Base date for the screenshot entries (May 2, 2025)
    const screenshotDate = new Date('2025-05-02');
    
    // Generate screenshot-matching entries
    const screenshotEntries = [
      {
        merchantId: partnersData[0]?.name || 'Tory Burch',
        merchantName: 'Tory Burch Solitaire',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 1990.00,
        refunds: 0.00,
        fees: 80.60,
        vat: 12.09,
        payoutAmount: 1897.31,
        balance: 0,
        currency: 'SAR',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[1]?.name || 'GMDC',
        merchantName: 'GMDC MUHAISNAH LLC',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 803.00,
        refunds: 0.00,
        fees: 37.13,
        vat: 1.86,
        payoutAmount: 713.76,
        balance: -50.25,
        currency: 'AED',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[2]?.name || 'ToYou',
        merchantName: 'ToYou Pi4 + PL',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 529055.71,
        refunds: 17142.79,
        fees: 16932.43,
        vat: 2539.17,
        payoutAmount: 493441.32,
        balance: 1000.00,
        currency: 'SAR',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[3]?.name || 'Decathlon',
        merchantName: 'Decathlon Kuwait - Mutla',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 412.000,
        refunds: 0.000,
        fees: 18.740,
        vat: 0.000,
        payoutAmount: 393.260,
        balance: 0,
        currency: 'KWD',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[4]?.name || 'EDI',
        merchantName: 'EDI',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 22521.60,
        refunds: 0.00,
        fees: 987.44,
        vat: 49.38,
        payoutAmount: 21484.78,
        balance: 0,
        currency: 'AED',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[5]?.name || 'Checkout.com',
        merchantName: 'Checkout.com KWT Adyen',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 100.000,
        refunds: 0.000,
        fees: 4.100,
        vat: 0.205,
        payoutAmount: 95.695,
        balance: 0,
        currency: 'KWD',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[6]?.name || 'TAKHAYAL',
        merchantName: 'TAKHAYAL mobile trading',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 2355.00,
        refunds: 0.00,
        fees: 166.11,
        vat: 8.31,
        payoutAmount: 2164.88,
        balance: -15.70,
        currency: 'AED',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[7]?.name || 'Jawhara',
        merchantName: 'Jawhara Madinat Zayed',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 3000.00,
        refunds: 0.00,
        fees: 166.00,
        vat: 8.30,
        payoutAmount: 2825.70,
        balance: 0,
        currency: 'AED',
        status: 'Paid' as const
      },
      {
        merchantId: partnersData[8]?.name || 'Michael Kors',
        merchantName: 'Michael Kors Rashidiya',
        type: 'Installments' as 'Installments',
        date: screenshotDate.toISOString().split('T')[0],
        formattedDate: 'May 2, 2025',
        time: '1:00 AM',
        sales: 2549.00,
        refunds: 0.00,
        fees: 103.96,
        vat: 15.60,
        payoutAmount: 2454.44,
        balance: 25.00,
        currency: 'SAR',
        status: 'Paid' as const
      }
    ];
    
    // Add screenshot entries to mock payouts
    screenshotEntries.forEach((entry, index) => {
      mockPayouts.push({
        id: `screenshot-${index}`,
        ...entry
      });
    });
    
    // Helper function to format date
    const formatDate = (date: Date): { formattedDate: string, time: string } => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const formattedDate = `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      const time = `${formattedHours}:${formattedMinutes} ${ampm}`;
      
      return { formattedDate, time };
    };
    
    // Generate random time for the day
    const getRandomTime = () => {
      const hours = Math.floor(Math.random() * 24);
      const minutes = Math.floor(Math.random() * 60);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${formattedHours}:${formattedMinutes} ${ampm}`;
    };
    
    // Generate additional random payouts across a range of dates
    // Start from 90 days before the screenshot date and end at the screenshot date
    const startDate = new Date(screenshotDate);
    startDate.setDate(startDate.getDate() - 90); // 90 days before
    
    // Generate multiple payouts for each partner
    partnersData.forEach((partner, partnerIndex) => {
      // Determine how many payouts to generate for this partner (2-5)
      const payoutCount = 2 + Math.floor(Math.random() * 4);
      
      for (let i = 0; i < payoutCount; i++) {
        // Generate a random date between startDate and screenshotDate
        const randomDaysOffset = Math.floor(Math.random() * 90);
        const payoutDate = new Date(startDate);
        payoutDate.setDate(payoutDate.getDate() + randomDaysOffset);
        
        // Skip if the date matches screenshot date for the first 9 partners (already have entries)
        if (
          payoutDate.toISOString().split('T')[0] === screenshotDate.toISOString().split('T')[0] &&
          partnerIndex < 9
        ) {
          continue;
        }
        
        const { formattedDate, time } = formatDate(payoutDate);
        const currency = currencyMap[partner.location] || 'AED';
        const conversionRate = currencyRates[currency] || 1;
        
        // Generate sales value - base it on partner's baseDebit with some randomness
        const baseValue = partner.baseDebit / conversionRate;
        const varianceFactor = 0.5 + Math.random();
        const sales = Math.round(baseValue * varianceFactor * 100) / 100;
        
        // Randomly determine if there are refunds (20% chance)
        let refunds = 0;
        if (Math.random() < 0.2) {
          refunds = Math.round(sales * (Math.random() * 0.15) * 100) / 100; // Refunds up to 15% of sales
        }
        
        // Calculate fees (3-5% of sales)
        const feeRate = 0.03 + Math.random() * 0.02;
        const fees = Math.round(sales * feeRate * 100) / 100;
        
        // Calculate VAT on fees (5% standard VAT rate)
        const vatRate = 0.05;
        const vat = Math.round(fees * vatRate * 100) / 100;
        
        // Generate balance
        let balance = 0;
        const balanceChance = Math.random();
        if (balanceChance < 0.1) { // 10% chance of negative balance
          balance = -(Math.round(sales * (Math.random() * 0.05) * 100) / 100); // Small negative balance
        } else if (balanceChance < 0.25) { // 15% chance of positive balance
          balance = Math.round(sales * (Math.random() * 0.1) * 100) / 100; // Small positive balance
        }
        
        // Calculate payout amount
        const payoutAmount = Math.round((sales - refunds - fees - vat + balance) * 100) / 100;
        
        // Determine status (Adjust distribution for new statuses)
        let status: PayoutItem['status'] = 'Paid'; // Default to 'Paid'
        const statusRandom = Math.random();
        if (statusRandom < 0.60) { // 60% Paid
          status = 'Paid';
        } else if (statusRandom < 0.80) { // 20% Unpaid
          status = 'Unpaid';
        } else if (statusRandom < 0.95) { // 15% In progress
          status = 'In progress';
        } else { // 5% Reversed
          status = 'Reversed';
        }
        
        // Determine type (65% Installments, 35% Acquiring)
        const type = Math.random() < 0.65 ? 'Installments' : 'Acquiring';
        
        // Create payout entry
        mockPayouts.push({
          id: `${partner.name}-${payoutDate.getTime()}-${i}`,
          merchantId: partner.name,
          merchantName: partner.name,
          type,
          date: payoutDate.toISOString().split('T')[0],
          formattedDate,
          time,
          sales,
          refunds,
          fees,
          vat,
          payoutAmount,
          balance,
          currency,
          status
        });
      }
    });
    
    // Generate additional custom entries (for a wider variety)
    // Define a list of custom merchant names for variety
    const customMerchantNames = [
      "AlJazira Electronics",
      "Desert Oasis Trading",
      "Gulf Star Retail",
      "Sahara Fashion House",
      "Falcon Sports Equipment",
      "Royal Jewelers Ltd",
      "Arabian Home & Décor",
      "Eastern Market Group",
      "Golden Sands Trading",
      "Sunrise Technology Center"
    ];
    
    // Generate 20 additional entries with custom merchant names
    for (let i = 0; i < 20; i++) {
      // Random date between Jan 1, 2025 and May 10, 2025
      const randomDate = new Date(2025, 0, 1);
      randomDate.setDate(randomDate.getDate() + Math.floor(Math.random() * 130));
      
      const { formattedDate, time } = formatDate(randomDate);
      
      // Randomly select a location for currency
      const locations = Object.keys(currencyMap);
      const randomLocation = locations[Math.floor(Math.random() * locations.length)];
      const currency = currencyMap[randomLocation] || 'AED';
      
      // Generate random values
      const sales = Math.round(1000 + Math.random() * 50000);
      
      // Randomly determine if there are refunds (30% chance)
      let refunds = 0;
      if (Math.random() < 0.3) {
        refunds = Math.round(sales * (Math.random() * 0.2) * 100) / 100; // Refunds up to 20% of sales
      }
      
      // Calculate fees (3-6% of sales)
      const feeRate = 0.03 + Math.random() * 0.03;
      const fees = Math.round(sales * feeRate * 100) / 100;
      
      // Calculate VAT on fees (5% standard VAT rate)
      const vatRate = 0.05;
      const vat = Math.round(fees * vatRate * 100) / 100;
      
      // Generate balance
      let balance = 0;
      const balanceChance = Math.random();
      if (balanceChance < 0.15) { // 15% chance of negative balance
        balance = -(Math.round(sales * (Math.random() * 0.07) * 100) / 100); // Small negative balance
      } else if (balanceChance < 0.35) { // 20% chance of positive balance
        balance = Math.round(sales * (Math.random() * 0.12) * 100) / 100; // Small positive balance
      }
      
      // Calculate payout amount
      const payoutAmount = Math.round((sales - refunds - fees - vat + balance) * 100) / 100;
      
      // Select status (Adjust distribution)
      let status: PayoutItem['status'] = 'Paid'; // Default Paid
      const statusRandom = Math.random();
      if (statusRandom < 0.55) { // 55% Paid
        status = 'Paid';
      } else if (statusRandom < 0.80) { // 25% Unpaid
        status = 'Unpaid';
      } else if (statusRandom < 0.95) { // 15% In progress
        status = 'In progress';
      } else { // 5% Reversed
        status = 'Reversed';
      }
      
      // Select random merchant name
      const merchantName = customMerchantNames[Math.floor(Math.random() * customMerchantNames.length)];
      
      // Determine type (60% Installments, 40% Acquiring)
      const type: 'Installments' | 'Acquiring' = Math.random() < 0.6 ? 'Installments' : 'Acquiring';
      
      // Create payout entry
      mockPayouts.push({
        id: `custom-${randomDate.getTime()}-${i}`,
        merchantId: merchantName,
        merchantName: merchantName,
        type,
        date: randomDate.toISOString().split('T')[0],
        formattedDate,
        time,
        sales,
        refunds,
        fees,
        vat,
        payoutAmount,
        balance,
        currency,
        status
      });
    }
    
    // Sort by date (newest first)
    mockPayouts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    // NEW: Set the first 50% of payouts to 'Unpaid'
    const halfIndex = Math.ceil(mockPayouts.length / 2);
    for (let i = 0; i < halfIndex; i++) {
      if (mockPayouts[i]) { // Check if the element exists
        mockPayouts[i].status = 'Unpaid';
      }
    }
    
    setPayouts(mockPayouts);
  }, []);

  // Apply filters when payouts data or filters change
  useEffect(() => {
    let filtered = [...payouts];
    
    // --- NEW: Date filtering logic based on TransactionsPage --- 
    const now = new Date();
    const calculateStartDate = (range: string): Date | null => {
      const date = new Date(now);
      switch (range) {
        case 'Last 7 Days':
          date.setDate(now.getDate() - 7);
          return date;
        case 'Last 30 Days':
          date.setDate(now.getDate() - 30);
          return date;
        case 'Last 90 Days':
          date.setDate(now.getDate() - 90);
          return date;
        case 'All Time':
        default:
          return null; // No start date for All Time
      }
    };

    const filterStartDate = calculateStartDate(selectedDateRange);

    // Filter by date range
    filtered = filtered.filter(payout => {
      const payoutDate = new Date(payout.date); // Use the YYYY-MM-DD date string
      payoutDate.setHours(0, 0, 0, 0); // Normalize time for date comparison

      if (selectedDateRange === 'Custom date') {
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999); // Include the whole end day
          return payoutDate >= start && payoutDate <= end;
        } else if (customStartDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          return payoutDate >= start;
        } else {
          return true; // No custom date range set, show all
        }
      } else if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        return payoutDate >= start;
      }
      return true; // All Time selected
    });
    // ----------------------------------------------------------
    
    // Filter by country
    if (filters.country) {
      filtered = filtered.filter(payout => partnerDetailsMap[payout.merchantId]?.location === filters.country);
    }

    // Filter by type
    if (filters.type) {
      filtered = filtered.filter(payout => payout.type === filters.type);
    }

    // NEW: Filter by status
    if (filters.status) {
      filtered = filtered.filter(payout => payout.status === filters.status);
    }
    
    setFilteredPayouts(filtered);
  }, [payouts, filters, partnerDetailsMap, selectedDateRange, customStartDate, customEndDate]);

  // Handle filter changes (excluding dates)
  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Handlers for date filters
  const handleDateRangeChange = (value: string) => {
    setSelectedDateRange(value);
    if (value === 'Custom date') {
      setIsDatePickerVisible(true);
    } else {
      setIsDatePickerVisible(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  // NEW: Get unpaid payouts for the payout flow
  const unpaidPayouts = useMemo(() => {
    return payouts.filter(payout => payout.status === 'Unpaid');
  }, [payouts]);

  // NEW: Handlers for payout flow
  const handleNewPayoutClick = () => {
    setIsPayoutSummaryModalOpen(true);
  };

  const handleConfirmPayout = () => {
    // NEW: Calculate details from unpaid payouts before closing summary modal
    const count = unpaidPayouts.length;
    const amountUSD = unpaidPayouts.reduce((sum, payout) => {
      const rate = currencyRates[payout.currency] || 1;
      return sum + payout.payoutAmount / rate;
    }, 0);
    setPayoutProcessDetails({ count, amountUSD }); // Store details

    setIsPayoutSummaryModalOpen(false);
    setIsProcessingModalOpen(true);
  };

  const handleFinishPayoutProcess = () => {
    setIsProcessingModalOpen(false);
    
    // Update the status of all unpaid payouts to 'Paid'
    setPayouts(prevPayouts => 
      prevPayouts.map(payout => 
        payout.status === 'Unpaid' 
          ? { ...payout, status: 'Paid' } 
          : payout
      )
    );
  };

  // NEW: Selected payout state
  const [selectedPayout, setSelectedPayout] = useState<PayoutItem | null>(null);
  // NEW: Panel visibility state for animation
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  // NEW: Content fade key for payout changes
  const [contentFadeKey, setContentFadeKey] = useState<string>('');

  // NEW: Handler for row click
  const handleRowClick = (payout: PayoutItem) => {
    if (selectedPayout) {
      // If a payout is already selected (panel is open), just update the payout
      // and trigger content fade animation with new key
      setContentFadeKey(payout.id);
      setSelectedPayout(payout);
    } else {
      // First time opening the panel
      setSelectedPayout(payout);
      setContentFadeKey(payout.id);
      // Set panel to visible with slight delay for proper animation when opening
      setTimeout(() => setIsPanelVisible(true), 10);
    }
  };

  // NEW: Handler to close the details panel
  const handleCloseDetailsPanel = () => {
    // First hide panel with animation
    setIsPanelVisible(false);
    // Then remove payout data after animation completes
    setTimeout(() => setSelectedPayout(null), 200);
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', backgroundColor: '#ffffff', minHeight: '100vh' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Payouts</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Manage payouts and view history</p>
          </div>
        </div>
        
        {/* Filters */}
        <PayoutFilters 
          filters={filters} 
          onFilterChange={handleFilterChange} 
          selectedDateRange={selectedDateRange}
          onDateRangeChange={handleDateRangeChange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomDateChange={handleCustomDateChange}
          isDatePickerVisible={isDatePickerVisible}
          countries={countries}
          types={types}
          statuses={statusOptions}
          onNewPayout={handleNewPayoutClick}
        />
        
        {/* Payouts Table - Updated with onRowClick */}
        <PayoutTable 
          payouts={filteredPayouts} 
          partnerDetailsMap={partnerDetailsMap} 
          onRowClick={handleRowClick} 
        />

        {/* NEW: Render Payout Details Panel conditionally */}
        {selectedPayout && (
          <PayoutDetailsPanel 
            payout={selectedPayout} 
            onClose={handleCloseDetailsPanel}
            isVisible={isPanelVisible}
            contentFadeKey={contentFadeKey}
          />
        )}

        {/* Payout Summary Modal */}
        {isPayoutSummaryModalOpen && (
          <ModalBackdrop onClose={() => setIsPayoutSummaryModalOpen(false)}>
            <PayoutSummaryModal 
              onClose={() => setIsPayoutSummaryModalOpen(false)} 
              onConfirm={handleConfirmPayout}
              unpaidPayouts={unpaidPayouts}
            />
          </ModalBackdrop>
        )}

        {/* Payout Processing Modal */}
        {isProcessingModalOpen && (
          <ModalBackdrop onClose={() => {}} preventClose={true}>
            <PayoutProcessingModal 
              onFinish={handleFinishPayoutProcess} 
              processedCount={payoutProcessDetails.count}
              processedAmountUSD={payoutProcessDetails.amountUSD}
            />
          </ModalBackdrop>
        )}
      </div>
    </MainLayout>
  );
};

export default PayoutsPage;
